import { ChevronDown, CircleDashed, Lock, type LucideIcon } from 'lucide-react'
import { observer } from 'mobx-react-lite'
import { useLayoutEffect, useRef, useState, type ReactNode, type WheelEvent } from 'react'
import { Link, useLocation } from 'react-router'
import { Badge } from '@/components/ui/Badge'
import { env } from '@/config/env'
import { prefetchPage } from '@/config/navigation'
import { useT } from '@/i18n'
import { cn } from '@/lib/cn'
import { useStores } from '@/stores'
import type { SessionStore } from '@/stores/SessionStore'
import { isOpenable, isVisible } from './access'
import type { MenuArea, MenuEntry, MenuNode } from './legacyMenu'
import { LogoMark } from './Logo'
import { entryActive, entryVisible, findActiveEntry, flatten, itemsByPath, menuText, nodeContains, nodeVisible, type Locale } from './menuModel'
import { SearchButton, TopbarActions } from './Topbar'

/*
 * Three-row top menu over the same legacy menu the sidebar shows:
 *   1. the logo (opens the dashboard) and the areas, General first, beside the
 *      account controls — pick an area to reveal its row below (no navigation);
 *   2. the area's pages and folders — a page navigates, a folder reveals row 3;
 *   3. the folder's (or a page's nested) pages.
 * Rows follow the current page until the user browses elsewhere; the browse
 * resets on navigation or Escape.
 */

const nodeKey = (node: MenuNode) => (node.kind === 'folder' ? node.id : `${node.path}?${node.search ?? ''}:${node.label}`)

/** What row 3 lists: pages, with nested folders flattened under a small heading. */
type Level3Item = { kind: 'heading'; key: string; label: string } | { kind: 'entry'; key: string; entry: MenuEntry }

function level3Items(nodes: MenuNode[], session: SessionStore, locale: Locale, out: Level3Item[] = []) {
  for (const node of nodes) {
    if (!nodeVisible(node, session)) continue
    if (node.kind === 'folder') {
      out.push({ kind: 'heading', key: node.id, label: menuText(node, locale) })
      level3Items(node.children, session, locale, out)
      continue
    }
    if (entryVisible(node, session)) out.push({ kind: 'entry', key: nodeKey(node), entry: node })
    if (node.children) level3Items(node.children, session, locale, out)
  }
  return out
}

/** A horizontal scroller: fades the edge that still hides content, scrolls with the wheel, keeps the selection in view. */
function Row({ label, watch, className, children }: { label: string; watch: string; className?: string; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    const node = ref.current
    if (!node) return
    const update = () => {
      node.dataset.fadeStart = String(node.scrollLeft > 1)
      node.dataset.fadeEnd = String(Math.ceil(node.scrollLeft + node.clientWidth) < node.scrollWidth - 1)
    }
    node.querySelector<HTMLElement>('[data-selected="true"]')?.scrollIntoView({ block: 'nearest', inline: 'nearest' })
    update()
    node.addEventListener('scroll', update, { passive: true })
    const observer = new ResizeObserver(update)
    observer.observe(node)
    return () => {
      node.removeEventListener('scroll', update)
      observer.disconnect()
    }
  }, [watch])

  const onWheel = (event: WheelEvent<HTMLDivElement>) => {
    if (Math.abs(event.deltaY) > Math.abs(event.deltaX)) event.currentTarget.scrollLeft += event.deltaY
  }

  return (
    <div ref={ref} role="group" aria-label={label} onWheel={onWheel} className={cn('edge-fade scrollbar-none flex min-w-0 items-center overflow-x-auto', className)}>
      {children}
    </div>
  )
}

// Rounded like the logo's own tiles (143/780 of its height), so the focus ring hugs them.
const logo =
  'relative flex shrink-0 items-center rounded-[calc(var(--spacing)*1.55)] transition-[opacity,transform] duration-[var(--dur-base)] hover:opacity-90 active:scale-[0.96]'
const pill = 'relative flex shrink-0 items-center gap-2 whitespace-nowrap rounded-[calc(10px*var(--radius-scale))] transition-[background-color,color,box-shadow,transform] duration-[var(--dur-base)] active:scale-[0.98]'
const pillOn = 'bg-glass-solid font-medium text-fg shadow-[0_1px_2px_rgba(0,0,0,0.06),inset_0_1px_0_var(--glass-highlight)] ring-1 ring-line'

/** Marks the area or folder holding the current page while another one is browsed. */
function HereDot() {
  return <span className="absolute bottom-0.5 left-1/2 h-0.5 w-3 -translate-x-1/2 rounded-full bg-accent" aria-hidden />
}

function Locked({ entry, label, className }: { entry: MenuEntry; label: string; className: string }) {
  const t = useT()
  const item = itemsByPath.get(entry.path)
  const Icon: LucideIcon = item?.icon ?? entry.icon ?? CircleDashed
  return (
    <span aria-disabled="true" title={item ? t('layout.sidebar.plannedHint') : t('layout.sidebar.soon')} className={cn(className, 'cursor-not-allowed text-muted')}>
      <Icon className="size-4 shrink-0 text-subtle" strokeWidth={1.75} />
      {label}
      <Lock className="size-3 shrink-0 text-subtle" aria-hidden />
      <span className="sr-only">{t('layout.sidebar.notAvailable')}</span>
    </span>
  )
}

const entryTo = (entry: MenuEntry) => ({ pathname: entry.path, search: entry.search ? `?${entry.search}` : '' })

function AreaTab({ area, locale, selected, current, compact, live, onSelect }: { area: MenuArea; locale: Locale; selected: boolean; current: boolean; compact: boolean; live: boolean; onSelect: () => void }) {
  const t = useT()
  const Icon = area.icon
  const label = menuText(area, locale)
  const showLabel = !compact || selected
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-expanded={selected}
      aria-current={current ? 'location' : undefined}
      data-selected={selected}
      title={label}
      aria-label={showLabel ? undefined : label}
      className={cn(
        pill,
        'h-8.5 text-[length:calc(13.5px*var(--text-scale))]',
        showLabel ? 'px-2.5' : 'w-8.5 justify-center',
        selected ? pillOn : live ? 'text-fg-2 hover:bg-hover hover:text-fg' : 'text-subtle hover:bg-hover',
      )}
    >
      <Icon className={cn('size-4 shrink-0', current ? 'text-accent' : live ? 'text-muted' : 'text-subtle')} strokeWidth={1.85} />
      {showLabel && label}
      {!live && <span className="sr-only">{t('layout.sidebar.soon')}</span>}
      {current && !selected && <HereDot />}
    </button>
  )
}

export const TopNav = observer(function TopNav() {
  const t = useT()
  const { ui, session } = useStores()
  const { pathname, search } = useLocation()
  const locale = ui.contentLocale
  const compact = ui.sidebarCollapsed
  const current = new URLSearchParams(search)
  const isActive = (entry: MenuEntry) => Boolean(itemsByPath.get(entry.path)) && entryActive(entry, pathname, current, session.menuAreas)
  const activeFlat = pathname === '/' ? undefined : findActiveEntry(session, isActive)
  const activeAreaId = activeFlat?.trail[0]

  // Browsing is tied to the route it started on, so any navigation drops it;
  // a menu link drops it too, even to the page already open (its URL stays).
  const routeKey = `${pathname}${search}`
  const [browse, setBrowse] = useState<{ route: string; area: string; node?: string | null } | null>(null)
  const browsing = browse?.route === routeKey ? browse : null

  const dashboard = itemsByPath.get('/')
  const dashboardOpen = Boolean(dashboard && isVisible(dashboard, session))
  // Settings open from the profile menu, so the navigation leaves them out.
  const areas = session.menuAreas.filter((candidate) => !candidate.inAccountMenu && candidate.children.some((node) => nodeVisible(node, session)))
  const area = areas.find((candidate) => candidate.id === (browsing?.area ?? activeAreaId))
  const level2 = area ? area.children.filter((node) => nodeVisible(node, session)) : []
  const activeLevel2 = activeFlat && area?.id === activeAreaId ? level2.find((node) => nodeContains(node, activeFlat.entry)) : undefined
  const selectedKey = browsing?.area === area?.id && browsing?.node !== undefined ? browsing.node : activeLevel2 && nodeKey(activeLevel2)
  const selectedNode = level2.find((node) => nodeKey(node) === selectedKey)
  const level3 = selectedNode?.children ? level3Items(selectedNode.children, session, locale) : []

  const areaLive = (candidate: MenuArea) =>
    flatten(candidate.children, []).some(({ entry }) => {
      const item = itemsByPath.get(entry.path)
      return item && isOpenable(item, session)
    })

  const selectArea = (id: string) => setBrowse({ route: routeKey, area: id })
  const selectFolder = (key: string) => area && setBrowse({ route: routeKey, area: area.id, node: key === selectedKey ? null : key })

  return (
    <nav
      aria-label={t('layout.sidebar.primary')}
      onKeyDown={(event) => event.key === 'Escape' && browsing && setBrowse(null)}
      className="glass glass-sheen shrink-0 overflow-hidden rounded-[var(--radius-shell)]"
    >
      {/* Level 1 — brand, areas and the account controls share one row */}
      <div className="nav-row-1 flex h-14 items-center gap-2 px-2.5">
        {/* The logo opens the dashboard. */}
        {dashboardOpen ? (
          <Link
            to="/"
            onMouseEnter={() => prefetchPage('/')}
            aria-current={pathname === '/' ? 'page' : undefined}
            aria-label={menuText('dashboard', locale)}
            title={menuText('dashboard', locale)}
            className={logo}
          >
            <LogoMark />
            {/* On the dashboard: the same accent bar that marks the current area. */}
            {pathname === '/' && <span className="absolute -bottom-1.75 left-1/2 h-0.5 w-4 -translate-x-1/2 rounded-full bg-accent" aria-hidden />}
          </Link>
        ) : (
          <span className={logo} aria-hidden>
            <LogoMark />
          </span>
        )}
        {!env.isProduction && (
          <Badge tone="warning" className="hidden h-5 min-w-0 px-1.5 text-[length:calc(10.5px*var(--text-scale))] uppercase tracking-[0.04em] 2xl:inline-flex">
            {env.appEnv}
          </Badge>
        )}
        <span className="mx-0.5 h-5 w-px shrink-0 bg-line" aria-hidden />
        {/* py leaves room for the focus ring, which the scroller would clip. */}
        <Row label={t('layout.topnav.areas')} watch={`${area?.id}:${compact}`} className="flex-1 gap-1 px-1 py-1.5">
          {areas.map((candidate) => (
            <AreaTab
              key={candidate.id}
              area={candidate}
              locale={locale}
              selected={candidate.id === area?.id}
              current={candidate.id === activeAreaId}
              compact={compact}
              live={areaLive(candidate)}
              onSelect={() => selectArea(candidate.id)}
            />
          ))}
        </Row>
        <SearchButton />
        <TopbarActions />
      </div>

      {/* Level 2 — the area's pages and folders */}
      <div className="collapsible" data-open={Boolean(area)} inert={!area}>
        <div>
          <Row label={t('layout.topnav.sections')} watch={`${area?.id}:${selectedKey}`} className="nav-row-2 gap-1 border-t border-line bg-hover/40 px-2 py-1.5">
            {area &&
              level2.map((node) => {
                const key = nodeKey(node)
                const selected = key === selectedKey
                const here = node === activeLevel2
                const base = cn(pill, 'h-7.5 px-2.5 text-[length:calc(13px*var(--text-scale))]')
                if (node.kind === 'folder') {
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => selectFolder(key)}
                      aria-expanded={selected}
                      data-selected={selected}
                      className={cn(base, 'gap-1.5 pr-2', selected ? pillOn : 'text-fg-2 hover:bg-hover hover:text-fg')}
                    >
                      {menuText(node, locale)}
                      <ChevronDown className={cn('size-3.5 text-subtle transition-transform duration-[var(--dur-base)]', !selected && '-rotate-90')} aria-hidden />
                      {here && !selected && <HereDot />}
                    </button>
                  )
                }
                const item = itemsByPath.get(node.path)
                const label = menuText(node, locale)
                if (!entryVisible(node, session)) return null
                if (!item || item.readiness === 'planned') return <Locked key={key} entry={node} label={label} className={base} />
                const active = isActive(node) && node === activeFlat?.entry
                return (
                  <Link
                    key={key}
                    to={entryTo(node)}
                    onClick={() => setBrowse(null)}
                    onMouseEnter={() => prefetchPage(node.path)}
                    onFocus={() => prefetchPage(node.path)}
                    aria-current={active ? 'page' : undefined}
                    data-selected={selected}
                    className={cn(base, active ? pillOn : 'text-fg-2 hover:bg-hover hover:text-fg')}
                  >
                    <item.icon className={cn('size-4 shrink-0', active ? 'text-accent' : 'text-muted')} strokeWidth={1.85} />
                    {label}
                    {item.readiness === 'transitional' && (
                      <span className="size-1.5 rounded-full bg-warning" title={t('layout.sidebar.transitional')} aria-label={t('layout.sidebar.transitional')} />
                    )}
                  </Link>
                )
              })}
          </Row>
        </div>
      </div>

      {/* Level 3 — pages inside the selected folder */}
      <div className="collapsible" data-open={level3.length > 0} inert={level3.length === 0}>
        <div>
          <Row label={t('layout.topnav.pages')} watch={`${selectedKey}:${routeKey}`} className="gap-5 border-t border-line px-4 py-1">
            {level3.map((node) => {
              if (node.kind === 'heading') {
                return (
                  <span key={node.key} className="shrink-0 whitespace-nowrap text-[length:calc(11px*var(--text-scale))] font-medium uppercase tracking-[0.06em] text-subtle">
                    {node.label}
                  </span>
                )
              }
              const { entry } = node
              const item = itemsByPath.get(entry.path)
              const label = menuText(entry, locale)
              const base = 'relative flex h-10 shrink-0 items-center gap-2 whitespace-nowrap text-[length:calc(13.5px*var(--text-scale))] transition-colors'
              if (!item || item.readiness === 'planned') return <Locked key={node.key} entry={entry} label={label} className={base} />
              const active = isActive(entry) && entry === activeFlat?.entry
              return (
                <Link
                  key={node.key}
                  to={entryTo(entry)}
                  onClick={() => setBrowse(null)}
                  onMouseEnter={() => prefetchPage(entry.path)}
                  onFocus={() => prefetchPage(entry.path)}
                  aria-current={active ? 'page' : undefined}
                  data-selected={active}
                  className={cn(base, active ? 'font-medium text-fg' : 'text-fg-2 hover:text-fg')}
                >
                  <item.icon className={cn('size-4 shrink-0', active ? 'text-accent' : 'text-muted')} strokeWidth={1.85} />
                  {label}
                  {item.readiness === 'transitional' && (
                    <span className="size-1.5 rounded-full bg-warning" title={t('layout.sidebar.transitional')} aria-label={t('layout.sidebar.transitional')} />
                  )}
                  {active && <span className="absolute inset-x-0 bottom-0 h-0.5 rounded-full bg-accent" aria-hidden />}
                </Link>
              )
            })}
          </Row>
        </div>
      </div>
    </nav>
  )
})
